import type { IcomxResourceDescriptor, IcomxResourceType } from 'src/services/api/icomx/actions';

export interface IcomxContactCandidate {
  id: string;
  label: string;
  subtitle?: string;
  resourceType: IcomxResourceType | 'TaskOwner' | 'ServiceRequestPerformer';
  resourceId?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  integrationId?: string;
  contactUuid?: string;
  telecomSummary: string[];
  sourceLabel?: string;
}

export type IcomxTelecomSystem = 'phone' | 'email';

export function hasIcomxCandidateTelecom(candidate: IcomxContactCandidate | null | undefined) {
  return !!candidate?.phone || !!candidate?.email;
}

export function hasAnyIcomxCandidateTelecom(candidates: IcomxContactCandidate[]) {
  return candidates.some(hasIcomxCandidateTelecom);
}

export function isIcomxLinkedResourceType(
  resourceType: IcomxContactCandidate['resourceType']
): resourceType is IcomxResourceType {
  return resourceType !== 'TaskOwner' && resourceType !== 'ServiceRequestPerformer';
}

export function getIcomxCandidateResourceDescriptor(
  candidate: IcomxContactCandidate | null | undefined,
  system?: IcomxTelecomSystem
): IcomxResourceDescriptor | null {
  if (!candidate?.resourceId || !isIcomxLinkedResourceType(candidate.resourceType)) {
    return null;
  }

  return {
    resourceType: candidate.resourceType,
    resourceId: candidate.resourceId,
    ...(system && candidate[system]
      ? {
          selectedTelecom: {
            system,
            value: candidate[system] as string,
          },
        }
      : {}),
  };
}

export function hasIcomxLinkedResource(candidate: IcomxContactCandidate | null | undefined) {
  return !!getIcomxCandidateResourceDescriptor(candidate);
}

export function filterIcomxCandidatesByTelecomSystem(
  candidates: IcomxContactCandidate[],
  system: IcomxTelecomSystem
) {
  return candidates.reduce<IcomxContactCandidate[]>((filteredCandidates, candidate) => {
    const telecomValue = candidate[system];

    if (!telecomValue) {
      return filteredCandidates;
    }

    return [
      ...filteredCandidates,
      {
        ...candidate,
        phone: system === 'phone' ? candidate.phone : undefined,
        email: system === 'email' ? candidate.email : undefined,
        telecomSummary: [telecomValue],
      },
    ];
  }, []);
}

export function filterLinkedIcomxCandidatesByTelecomSystem(
  candidates: IcomxContactCandidate[],
  system: IcomxTelecomSystem
) {
  return filterIcomxCandidatesByTelecomSystem(candidates, system).filter((candidate) =>
    hasIcomxLinkedResource(candidate)
  );
}

export function dedupeIcomxContactCandidates(candidates: IcomxContactCandidate[]) {
  return candidates.reduce<IcomxContactCandidate[]>((uniqueCandidates, candidate) => {
    if (uniqueCandidates.some(({ id }) => id === candidate.id)) {
      return uniqueCandidates;
    }

    return [...uniqueCandidates, candidate];
  }, []);
}

export function mergeIcomxContactCandidates(candidates: IcomxContactCandidate[]) {
  return candidates.reduce<IcomxContactCandidate[]>((mergedCandidates, candidate) => {
    const existingCandidateIndex = mergedCandidates.findIndex(({ id }) => id === candidate.id);

    if (existingCandidateIndex === -1) {
      return [...mergedCandidates, candidate];
    }

    const existingCandidate = mergedCandidates[existingCandidateIndex];
    const nextCandidate = {
      ...existingCandidate,
      ...candidate,
      subtitle: existingCandidate.subtitle ?? candidate.subtitle,
      resourceId: existingCandidate.resourceId ?? candidate.resourceId,
      phone: existingCandidate.phone ?? candidate.phone,
      email: existingCandidate.email ?? candidate.email,
      integrationId: existingCandidate.integrationId ?? candidate.integrationId,
      contactUuid: existingCandidate.contactUuid ?? candidate.contactUuid,
      sourceLabel: existingCandidate.sourceLabel ?? candidate.sourceLabel,
      telecomSummary: Array.from(
        new Set([...(existingCandidate.telecomSummary ?? []), ...(candidate.telecomSummary ?? [])])
      ),
    };

    return mergedCandidates.map((mergedCandidate, index) =>
      index === existingCandidateIndex ? nextCandidate : mergedCandidate
    );
  }, []);
}
